import { useEffect, useState } from 'react'
import { Building2, Globe, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { useBranch } from '@/contexts/BranchContext'
import { fetchBranch, updateBranch } from '@/lib/api/branches'
import { fetchCompany, updateCompany } from '@/lib/api/company'
import {
  fetchOnlineBookingSettings,
  updateOnlineBookingSettings,
} from '@/lib/api/onlineBookingSettings'
import { cn } from '@/lib/utils'

import { SettingsSection } from './SettingsSection'

const inputClass = cn(
  'h-11 w-full rounded-xl border border-border/80 bg-card px-4 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

function instagramFromLinks(links) {
  return links?.find((link) => link.key === 'instagram')?.url ?? ''
}

function buildInstagramLink(url) {
  const trimmed = url?.trim()
  if (!trimmed) return []
  return [{ key: 'instagram', label: 'Instagram', url: trimmed }]
}

function buildOnlineTitleLines(name, secondLine) {
  return [name, secondLine].map((line) => line?.trim()).filter(Boolean)
}

export function SalonSettingsSection({ embedded = false }) {
  const { refreshUser } = useAuth()
  const { activeBranchId } = useBranch()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState({
    name: '',
    phone: '',
    whatsapp: '',
    address: '',
    mapUrl: '',
    logo: '',
    onlineTitleLine2: '',
    onlineTagline: '',
    coverImageUrl: '',
    instagramUrl: '',
  })

  useEffect(() => {
    if (!activeBranchId) {
      return undefined
    }

    let active = true
    setLoading(true)
    setError('')

    async function load() {
      try {
        const [branch, company, online] = await Promise.all([
          fetchBranch(activeBranchId),
          fetchCompany(),
          fetchOnlineBookingSettings().catch(() => null),
        ])
        if (!active) {
          return
        }
        const titleLines = online?.hero_title_lines ?? []
        setForm({
          name: branch.name ?? '',
          phone: branch.phone ?? '',
          whatsapp: branch.whatsapp ?? online?.contact?.whatsapp ?? '',
          address: branch.address ?? '',
          mapUrl: branch.map_url ?? online?.contact?.map_url ?? '',
          logo: company.logo ?? online?.logo_url ?? '',
          onlineTitleLine2: titleLines[1] ?? '',
          onlineTagline: online?.hero_subline ?? 'BOOK & RELAX',
          coverImageUrl: online?.hero_image_url ?? '',
          instagramUrl: instagramFromLinks(online?.social_links),
        })
      } catch (err) {
        if (active) {
          setError(err?.response?.data?.message ?? 'Unable to load salon details.')
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      active = false
    }
  }, [activeBranchId])

  async function handleSubmit(event) {
    event.preventDefault()
    if (!activeBranchId) {
      return
    }

    setSubmitting(true)
    setError('')
    setSuccess('')

    try {
      const titleLines = buildOnlineTitleLines(form.name, form.onlineTitleLine2)

      await Promise.all([
        updateBranch(activeBranchId, {
          name: form.name,
          phone: form.phone || null,
          whatsapp: form.whatsapp || null,
          address: form.address || null,
          map_url: form.mapUrl || null,
        }),
        updateCompany({
          logo: form.logo || null,
        }),
        updateOnlineBookingSettings({
          logo_url: form.logo || null,
          hero_image_url: form.coverImageUrl || null,
          hero_title_lines: titleLines.length > 0 ? titleLines : [form.name],
          hero_subline: form.onlineTagline.trim() || 'BOOK & RELAX',
          phone: form.phone || null,
          whatsapp: form.whatsapp || null,
          address: form.address || null,
          map_url: form.mapUrl || null,
          social_links: buildInstagramLink(form.instagramUrl),
        }),
      ])
      await refreshUser()
      setSuccess('Salon details saved.')
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Unable to save salon details.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <SettingsSection
      id="salon"
      icon={Building2}
      title="Salon"
      description="Your salon details and online booking page"
      defaultOpen
      embedded={embedded}
    >
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="salon-name" className="mb-1.5 block text-sm font-medium">
              Salon name
            </label>
            <input
              id="salon-name"
              type="text"
              value={form.name}
              onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
              className={inputClass}
              required
            />
          </div>
          <div>
            <label htmlFor="salon-phone" className="mb-1.5 block text-sm font-medium">
              Phone
            </label>
            <input
              id="salon-phone"
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((current) => ({ ...current, phone: e.target.value }))}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="salon-address" className="mb-1.5 block text-sm font-medium">
              Address
            </label>
            <textarea
              id="salon-address"
              rows={2}
              value={form.address}
              onChange={(e) => setForm((current) => ({ ...current, address: e.target.value }))}
              className={cn(inputClass, 'h-auto py-3')}
            />
          </div>
          <div>
            <label htmlFor="salon-whatsapp" className="mb-1.5 block text-sm font-medium">
              WhatsApp
            </label>
            <input
              id="salon-whatsapp"
              type="tel"
              value={form.whatsapp}
              onChange={(e) => setForm((current) => ({ ...current, whatsapp: e.target.value }))}
              className={inputClass}
              placeholder="+971 50 …"
            />
          </div>
          <div>
            <label htmlFor="salon-logo" className="mb-1.5 block text-sm font-medium">
              Logo URL
            </label>
            <input
              id="salon-logo"
              type="url"
              value={form.logo}
              onChange={(e) => setForm((current) => ({ ...current, logo: e.target.value }))}
              className={inputClass}
              placeholder="https://…"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Shown on receipts and your public booking page.
            </p>
          </div>

          <div className="rounded-xl border border-border/60 bg-muted/15 px-4 py-3">
            <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
              <Globe className="h-4 w-4 text-primary" aria-hidden />
              Online booking page
            </p>
            <p className="mb-4 text-xs text-muted-foreground">
              Guests see these on your book-online link — same phone and address as above.
            </p>
            <div className="space-y-4">
              <div>
                <label htmlFor="online-title-2" className="mb-1.5 block text-sm font-medium">
                  Extra title line (optional)
                </label>
                <input
                  id="online-title-2"
                  type="text"
                  value={form.onlineTitleLine2}
                  onChange={(e) => setForm((current) => ({ ...current, onlineTitleLine2: e.target.value }))}
                  className={inputClass}
                  placeholder="e.g. Dubai Marina"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Main title uses salon name: {form.name || '…'}
                </p>
              </div>
              <div>
                <label htmlFor="online-tagline" className="mb-1.5 block text-sm font-medium">
                  Tagline
                </label>
                <input
                  id="online-tagline"
                  type="text"
                  value={form.onlineTagline}
                  onChange={(e) => setForm((current) => ({ ...current, onlineTagline: e.target.value }))}
                  className={inputClass}
                  placeholder="BOOK & RELAX"
                />
              </div>
              <div>
                <label htmlFor="cover-image" className="mb-1.5 block text-sm font-medium">
                  Cover image URL
                </label>
                <input
                  id="cover-image"
                  type="url"
                  value={form.coverImageUrl}
                  onChange={(e) => setForm((current) => ({ ...current, coverImageUrl: e.target.value }))}
                  className={inputClass}
                  placeholder="https://…/hero.jpg"
                />
              </div>
              <div>
                <label htmlFor="instagram-url" className="mb-1.5 block text-sm font-medium">
                  Instagram
                </label>
                <input
                  id="instagram-url"
                  type="url"
                  value={form.instagramUrl}
                  onChange={(e) => setForm((current) => ({ ...current, instagramUrl: e.target.value }))}
                  className={inputClass}
                  placeholder="https://instagram.com/…"
                />
              </div>
              <div>
                <label htmlFor="map-url" className="mb-1.5 block text-sm font-medium">
                  Map link
                </label>
                <input
                  id="map-url"
                  type="url"
                  value={form.mapUrl}
                  onChange={(e) => setForm((current) => ({ ...current, mapUrl: e.target.value }))}
                  className={inputClass}
                  placeholder="Google Maps URL"
                />
              </div>
            </div>
          </div>
          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="text-sm text-primary" role="status">
              {success}
            </p>
          ) : null}
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save salon details'}
          </Button>
        </form>
      )}
    </SettingsSection>
  )
}
